/* ============================================================
   ALVETTE — shared front-end helpers
   No dependencies. Safe to load in <head> with defer.
   Every helper is guarded: missing elements are a no-op.
   ============================================================ */
(function () {
  'use strict';

  var reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  /* ---------- 1. Scroll reveal ---------- */
  function initReveal(root) {
    var els = (root || document).querySelectorAll('.alv-reveal:not([data-reveal-bound])');
    if (!els.length) return;

    if (reduceMotion || !('IntersectionObserver' in window)) {
      els.forEach(function (el) {
        el.setAttribute('data-reveal-bound', '');
        el.classList.add('is-in');
      });
      return;
    }

    var io = new IntersectionObserver(
      function (entries) {
        entries.forEach(function (entry) {
          if (!entry.isIntersecting) return;
          entry.target.classList.add('is-in');
          io.unobserve(entry.target);
        });
      },
      { rootMargin: '0px 0px -8% 0px', threshold: 0.05 }
    );

    els.forEach(function (el) {
      el.setAttribute('data-reveal-bound', '');
      io.observe(el);
    });
  }

  /* ---------- 2. Header height -> --alv-header-h ----------
     Sections below a sticky header need its real height.      */
  function syncHeaderHeight() {
    var header = document.querySelector('[data-alv-header]');
    if (!header) return;
    var apply = function () {
      document.documentElement.style.setProperty('--alv-header-h', header.offsetHeight + 'px');
    };
    apply();
    if ('ResizeObserver' in window) new ResizeObserver(apply).observe(header);
    else window.addEventListener('resize', apply, { passive: true });
  }

  /* ---------- 3. Body scroll lock (drawer / modal) ----------
     Preserves scroll position — iOS Safari loses it otherwise. */
  var scrollY = 0;
  var lockCount = 0;

  function lockScroll() {
    if (lockCount++ > 0) return;
    scrollY = window.scrollY;
    document.body.style.position = 'fixed';
    document.body.style.top = -scrollY + 'px';
    document.body.style.left = '0';
    document.body.style.right = '0';
    document.body.style.width = '100%';
  }

  function unlockScroll() {
    if (--lockCount > 0) return;
    lockCount = 0;
    document.body.style.position = '';
    document.body.style.top = '';
    document.body.style.left = '';
    document.body.style.right = '';
    document.body.style.width = '';
    window.scrollTo(0, scrollY);
  }

  /* ---------- 4. Focus trap ---------- */
  var FOCUSABLE =
    'a[href],button:not([disabled]),input:not([disabled]),select:not([disabled]),' +
    'textarea:not([disabled]),summary,[tabindex]:not([tabindex="-1"])';

  function trapFocus(container, event) {
    if (event.key !== 'Tab') return;
    var nodes = Array.prototype.filter.call(
      container.querySelectorAll(FOCUSABLE),
      function (el) { return el.offsetParent !== null; }
    );
    if (!nodes.length) return;
    var first = nodes[0];
    var last = nodes[nodes.length - 1];
    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  }

  /* ---------- 5. Swipe detection ---------- */
  function onSwipe(el, handler, threshold) {
    if (!el) return;
    var min = threshold || 40;
    var x0 = null;
    var y0 = null;

    el.addEventListener('touchstart', function (e) {
      var t = e.changedTouches[0];
      x0 = t.clientX;
      y0 = t.clientY;
    }, { passive: true });

    el.addEventListener('touchend', function (e) {
      if (x0 === null) return;
      var t = e.changedTouches[0];
      var dx = t.clientX - x0;
      var dy = t.clientY - y0;
      x0 = null;
      // Ignore mostly-vertical gestures so page scrolling still works.
      if (Math.abs(dx) < min || Math.abs(dx) < Math.abs(dy) * 1.4) return;
      handler(dx < 0 ? 'left' : 'right');
    }, { passive: true });
  }

  /* ---------- 6. Expose ---------- */
  window.Alvette = {
    initReveal: initReveal,
    lockScroll: lockScroll,
    unlockScroll: unlockScroll,
    trapFocus: trapFocus,
    onSwipe: onSwipe,
    reduceMotion: reduceMotion,
    FOCUSABLE: FOCUSABLE
  };

  /* ---------- 7. Boot ---------- */
  function boot() {
    initReveal(document);
    syncHeaderHeight();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }

  // Shopify theme editor re-renders sections without a page load.
  document.addEventListener('shopify:section:load', function (e) {
    initReveal(e.target);
    syncHeaderHeight();
  });
})();
